import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Send, MapPin, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t('contact_success'));
      setForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fac-dark pt-16">
      {/* Hero */}
      <section className="py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white tracking-tighter uppercase mb-4">
              {t('contact_title')}
            </h1>
            <p className="text-2xl text-fac-green font-chinese">
              聯絡我們
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-white uppercase mb-6">
                GET IN TOUCH
                <span className="block text-fac-green font-chinese text-lg mt-1">
                  與我們聯繫
                </span>
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-fac-green/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-fac-green" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-1">
                      Location / 地點
                    </h4>
                    <p className="text-muted-foreground font-chinese">
                      Global / 全球服務
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-fac-green/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-fac-green" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-1">
                      Email / 電子郵件
                    </h4>
                    <p className="text-muted-foreground">
                      info@facsolutions.com
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-fac-card border border-border rounded-lg p-6">
                <h4 className="font-heading font-bold text-white uppercase mb-2">
                  Business Hours / 營業時間
                </h4>
                <p className="text-muted-foreground font-chinese">
                  Monday - Friday: 9:00 AM - 6:00 PM (UTC+8)
                  <br />
                  週一至週五：上午 9:00 - 下午 6:00 (台北時間)
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-fac-card border border-border rounded-lg p-8 cyber-border">
              <h2 className="font-heading font-bold text-2xl text-white uppercase mb-6">
                SEND A MESSAGE
                <span className="block text-fac-green font-chinese text-lg mt-1">
                  發送訊息
                </span>
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-white font-chinese">
                    {t('contact_name')} / 姓名
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white h-12"
                    placeholder="Your name / 您的姓名"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-white font-chinese">
                    {t('contact_email')} / 電子郵件
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white h-12"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-white font-chinese">
                    {t('contact_message')} / 訊息
                  </Label>
                  <Textarea
                    id="message"
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white min-h-[150px]"
                    placeholder="Your message / 您的訊息"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14"
                  data-testid="contact-submit-btn"
                >
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    {loading ? t('loading') : t('contact_send')}
                  </span>
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
